import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { NewsListItem, getLatestNews, newsImageUrl } from "../api/news";

/** Landing widget: shows the three latest published news. Renders nothing when empty. */
export default function LatestNewsSection() {
  const { t, language } = useLanguage();
  const [items, setItems] = useState<NewsListItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    getLatestNews(3)
      .then((news) => {
        if (active) setItems(news);
      })
      .catch(() => {
        if (active) setItems([]);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  if (!loaded || items.length === 0) return null;

  const title = (i: NewsListItem) =>
    language === "kk" ? i.title_kk || i.title_ru : i.title_ru || i.title_kk;
  const excerpt = (i: NewsListItem) =>
    language === "kk"
      ? i.excerpt_kk || i.excerpt_ru
      : i.excerpt_ru || i.excerpt_kk;
  const catTitle = (i: NewsListItem) =>
    language === "kk"
      ? i.category_title_kk || i.category_title_ru
      : i.category_title_ru || i.category_title_kk;
  const fmtDate = (iso: string | null) => {
    if (!iso) return "";
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
      ? ""
      : date.toLocaleDateString(language === "kk" ? "kk-KZ" : "ru-RU", {
          day: "numeric",
          month: "long",
        });
  };

  return (
    <section
      className="landing-section bg-white"
      aria-labelledby="landing-news-title"
    >
      <div className="landing-container">
        <div className="mb-9 flex flex-wrap items-end justify-between gap-x-8 gap-y-5">
          <div>
            <p className="landing-eyebrow">{t("news.sectionTag")}</p>
            <h2
              id="landing-news-title"
              className="text-[1.75rem] font-semibold leading-tight tracking-tight text-[#172b46] sm:text-4xl"
            >
              {t("news.sectionTitle")}
            </h2>
          </div>
          <Link
            to="/news"
            className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-goszakup-700 hover:underline hover:underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-goszakup-700"
          >
            {t("news.sectionAll")}{" "}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>

        <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((post) => {
            const cover = newsImageUrl(post.cover_url);
            const category = catTitle(post);
            const publishedDate = fmtDate(post.published_at);

            return (
              <article
                key={post.slug}
                className="min-w-0 border-t border-[#d9e0e5] pt-5"
              >
                <Link
                  to={`/news/${post.slug}`}
                  className="group flex h-full flex-col text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-goszakup-700"
                >
                  <div className="mb-5 aspect-video overflow-hidden rounded-xl bg-slate-50">
                    {cover ? (
                      <img
                        src={cover}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div
                        className="flex h-full w-full items-center justify-center"
                        aria-hidden="true"
                      >
                        <Newspaper
                          className="h-9 w-9 text-[#9aa7b4]"
                          strokeWidth={1.25}
                        />
                      </div>
                    )}
                  </div>
                  <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs leading-relaxed text-[#596979]">
                    {category && (
                      <span className="font-medium text-goszakup-700">
                        {category}
                      </span>
                    )}
                    {publishedDate && (
                      <time dateTime={post.published_at || undefined}>
                        {publishedDate}
                      </time>
                    )}
                  </div>
                  <h3 className="mb-3 text-xl font-semibold leading-snug tracking-tight group-hover:text-goszakup-700 group-hover:underline group-hover:underline-offset-4">
                    {title(post)}
                  </h3>
                  <p className="mb-5 line-clamp-3 text-sm leading-6 text-[#596979]">
                    {excerpt(post)}
                  </p>
                  {post.reading_time > 0 && (
                    <span className="mt-auto text-xs text-[#596979]">
                      {post.reading_time} {t("news.min")}
                    </span>
                  )}
                </Link>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
